import { parse } from 'yaml'

import { parsePhysics, PHYSICS_DEFAULTS, type Physics } from './physics'

export interface RuntimeConfig {
  lists: { pageSize: number; pageSizes: number[] }
  dashboard: { defaultPeriod: number; refreshMs: number }
  search: { debounceMs: number }
  graph: { defaultPeriodDays: number; physics: Physics }
}

const DEFAULTS: RuntimeConfig = {
  lists: { pageSize: 50, pageSizes: [25, 50, 100] },
  dashboard: { defaultPeriod: 30, refreshMs: 60_000 },
  search: { debounceMs: 250 },
  graph: { defaultPeriodDays: 30, physics: PHYSICS_DEFAULTS },
}

let current: RuntimeConfig = DEFAULTS

export function config(): RuntimeConfig {
  return current
}

function ms(v: unknown, fallback: number): number {
  if (typeof v === 'number' && v >= 0) return v * 1000
  if (typeof v !== 'string') return fallback
  const m = /^(\d+(?:\.\d+)?)(ms|s|m)$/.exec(v.trim())
  if (!m) return fallback
  const n = Number(m[1])
  return m[2] === 'ms' ? n : m[2] === 's' ? n * 1000 : n * 60_000
}

function posInt(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isInteger(v) && v > 0 ? v : fallback
}

function intList(v: unknown, fallback: number[]): number[] {
  if (!Array.isArray(v)) return fallback
  const list = v.filter((x): x is number => typeof x === 'number' && Number.isInteger(x) && x > 0)
  return list.length ? list : fallback
}

type Raw = Record<string, Record<string, unknown> | undefined>

export async function loadConfig(): Promise<RuntimeConfig> {
  try {
    const res = await fetch('/config.yml', { cache: 'no-store' })
    if (!res.ok) throw new Error(`HTTP ${String(res.status)}`)
    const raw = (parse(await res.text()) ?? {}) as Raw
    const pageSizes = intList(raw.lists?.page_sizes, DEFAULTS.lists.pageSizes)
    const pageSize = posInt(raw.lists?.page_size, DEFAULTS.lists.pageSize)
    current = {
      lists: { pageSize, pageSizes: pageSizes.includes(pageSize) ? pageSizes : [...pageSizes, pageSize].sort((a, b) => a - b) },
      dashboard: {
        defaultPeriod: Math.min(366, posInt(raw.dashboard?.default_period, DEFAULTS.dashboard.defaultPeriod)),
        refreshMs: ms(raw.dashboard?.refresh_interval, DEFAULTS.dashboard.refreshMs),
      },
      search: { debounceMs: ms(raw.search?.debounce, DEFAULTS.search.debounceMs) },
      graph: {
        defaultPeriodDays: Math.min(366, posInt(raw.graph?.default_period_days, DEFAULTS.graph.defaultPeriodDays)),
        physics: parsePhysics(raw.graph),
      },
    }
  } catch (e) {
    console.warn('config.yml not loaded, using defaults:', e)
  }
  return current
}
